// src/components/ShareModal.js

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { supabase } from '../../supabase';
import bcrypt from 'bcryptjs';
import toast from 'react-hot-toast';
import { encryptText } from '../../utils/crypto';
import './ShareModal.css';

Modal.setAppElement('#root');

export const ShareModal = ({ isOpen, onRequestClose, file }) => {
  const [hours, setHours] = useState(1);
  const [minutes, setMinutes] = useState(0);
  const [allowDownload, setAllowDownload] = useState(true);
  const [password, setPassword] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeLinks, setActiveLinks] = useState([]);
  const [, setTick] = useState(0);

  // Live timer interval to update countdowns every 1s
  useEffect(() => {
    let interval = null;
    if (isOpen) {
      interval = setInterval(() => {
        setTick(t => t + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOpen]);

  // Fetch existing active share links for this file
  const fetchActiveLinks = React.useCallback(async () => {
    if (!file || !isOpen) return;
    try {
      const nowString = new Date().toISOString();
      
      // 1. Automatically delete expired links for this file in the background to save database space
      await supabase
        .from('sharedLinks')
        .delete()
        .eq('fileId', file.id)
        .lt('expiresAt', nowString);

      // 2. Fetch only the active (non-expired) links
      const { data, error } = await supabase
        .from('sharedLinks')
        .select('*')
        .eq('fileId', file.id)
        .gt('expiresAt', nowString)
        .order('expiresAt', { ascending: false });

      if (!error && data) {
        setActiveLinks(data);
      }
    } catch (err) {
      console.error("Error fetching/cleaning share links:", err);
    }
  }, [file, isOpen]);

  useEffect(() => {
    fetchActiveLinks();
  }, [fetchActiveLinks]);

  if (!file) {
    return null;
  }

  const getExpiryDate = () => {
    const now = new Date();
    const hrs = parseInt(hours, 10) || 0; 
    const mins = parseInt(minutes, 10) || 0;

    if (hrs < 0 || mins < 0) {
        toast.error("Time values cannot be negative.");
        return null;
    }
    if (hrs === 0 && mins === 0) {
        toast.error("Link duration cannot be zero.");
        return null;
    }

    now.setHours(now.getHours() + hrs);
    now.setMinutes(now.getMinutes() + mins);
    
    return now.toISOString();
  };

  const handleGenerateLink = async () => {
    const expiresAt = getExpiryDate();
    if (!expiresAt) return;

    setIsGenerating(true);
    const toastId = toast.loading("Generating link...");
    
    const originalUrl = file.fileUrl;
    if (!originalUrl) {
        toast.dismiss(toastId);
        toast.error("Error: Could not find the file's URL.");
        setIsGenerating(false); return;
    }
    
    let passwordHash = null;
    let finalUrl = originalUrl;
    try {
        if (password) {
            passwordHash = await bcrypt.hash(password, 12);
            // Securely encrypt the file URL client-side with the password
            finalUrl = await encryptText(originalUrl, password);
        }
    } catch (cryptoErr) {
        toast.dismiss(toastId);
        toast.error("Security error during link generation.");
        setIsGenerating(false); return;
    }

    try {
        const { data, error } = await supabase.from('sharedLinks').insert([{
            fileId: file.id, fileName: file.fileName, originalUrl: finalUrl,
            uid: file.uid, expiresAt: expiresAt,
            allowDownload: allowDownload, passwordHash: passwordHash
        }]).select();
        
        if (error) throw error;
        
        const docRef = data[0];
        const newLink = `${window.location.origin}/share/${docRef.id}`;
        
        // Auto copy generated link and notify user
        await navigator.clipboard.writeText(newLink);
        toast.dismiss(toastId);
        toast.success("Secure link generated & copied to clipboard!");
        
        // Reset generation inputs
        setPassword('');
        setHours(1);
        setMinutes(0);
        setAllowDownload(true);

        // Instantly push to UI state to show immediately in the list
        setActiveLinks(prev => [docRef, ...prev]);
    } catch (error) {
        console.error("Error creating share link:", error);
        toast.dismiss(toastId);
        toast.error("Could not create share link.");
    } finally {
        setIsGenerating(false);
    }
  };

  const handleRevokeLink = async (linkId) => {
    const toastId = toast.loading("Revoking link...");
    try {
      // 1. Optimistic UI update: instantly hide from screen
      setActiveLinks(prev => prev.filter(link => link.id !== linkId));

      const { error } = await supabase
        .from('sharedLinks')
        .delete()
        .eq('id', linkId);

      if (error) {
        // If Supabase RLS or database failed, restore the item by re-fetching
        fetchActiveLinks();
        throw error;
      }

      toast.dismiss(toastId);
      toast.success("Link revoked successfully and is now invalid!");
    } catch (err) {
      console.error("Revoke error details:", err);
      toast.dismiss(toastId);
      toast.error(`Failed to revoke link: ${err.message || 'Verification failed'}`);
    }
  };

  const handleClose = () => {
    setPassword('');
    setHours(1); 
    setMinutes(0);
    setAllowDownload(true);
    onRequestClose();
  };
  
  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard!");
  };

  const getCountdownText = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return "Expired";

    const secs = Math.floor(diff / 1000) % 60;
    const mins = Math.floor(diff / (1000 * 60)) % 60;
    const hrs = Math.floor(diff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hrs > 0) parts.push(`${hrs}h`);
    if (mins > 0) parts.push(`${mins}m`);
    parts.push(`${secs}s`);

    return parts.join(' ');
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onRequestClose={handleClose} 
      contentLabel="Share File Modal" 
      className="modal" 
      overlayClassName="overlay"
      style={{ content: { maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto' } }}
    >
      <button className="modal-close-btn" onClick={handleClose} aria-label="Close modal">&times;</button>
      <h2>Share "{file.fileName}"</h2>
      
      <div className="share-form-section" style={{ marginBottom: '25px' }}>
          <h4 style={{ marginBottom: '15px', color: 'var(--color-text-secondary)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.05em' }}>Create Secure Share Link</h4>
          <div className="share-option-row">
              <label>Link Expires In:</label>
              <div className="time-picker-container">
                  <div className="time-input-group">
                      <input type="number" id="hours" min="0" value={hours} onChange={(e) => setHours(e.target.value)} />
                      <label htmlFor="hours">Hours</label>
                  </div>
                  <div className="time-input-group">
                      <input type="number" id="minutes" min="0" max="59" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
                      <label htmlFor="minutes">Minutes</label>
                  </div>
              </div>
          </div>

          <div className="share-option-row">
              <label htmlFor="allowDownload">Allow Download:</label>
              <input type="checkbox" id="allowDownload" checked={allowDownload} onChange={(e) => setAllowDownload(e.target.checked)} />
          </div>
          <div className="share-option-row">
              <label htmlFor="password">Password (optional):</label>
              <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank for no password" />
          </div>
          <div className="modal-actions" style={{ padding: 0, marginTop: '15px' }}>
              <button className="generate-button" onClick={handleGenerateLink} disabled={isGenerating}>
                  {isGenerating ? 'Generating...' : 'Generate Share Link'}
              </button>
          </div>
      </div>

      <hr className="share-separator" style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '20px 0' }} />

      <div className="active-links-section">
        <h4 style={{ marginBottom: '15px', color: 'var(--color-text-secondary)', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.05em' }}>Active Secure Links</h4>
        {activeLinks.filter(link => new Date(link.expiresAt) > new Date()).length > 0 ? (
          <div className="active-links-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {activeLinks
              .filter(link => new Date(link.expiresAt) > new Date())
              .map(link => {
                const countdownText = getCountdownText(link.expiresAt);
                const shareUrl = `${window.location.origin}/share/${link.id}`;

                return (
                  <div key={link.id} className="active-link-item" style={{
                    padding: '14px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-background)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#28a745'
                      }}>
                        ⏳ Expires in {countdownText}
                      </span>
                      <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}>
                        {link.passwordHash ? '🔒 Protected' : '🔓 Public'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input 
                        type="text" 
                        readOnly 
                        value={shareUrl} 
                        style={{ 
                          flex: 1, 
                          padding: '6px 10px', 
                          fontSize: '12px', 
                          borderRadius: '4px', 
                          border: '1px solid var(--color-border)', 
                          backgroundColor: 'rgba(var(--color-primary-rgb), 0.08)',
                          color: 'var(--color-text-primary)'
                        }}
                        onClick={(e) => e.target.select()}
                      />
                      <button 
                        className="action-button save-button" 
                        onClick={() => copyLink(shareUrl)} 
                        style={{ padding: '6px 12px', fontSize: '12px', margin: 0 }}
                      >
                        Copy
                      </button>
                      <button 
                        className="action-button cancel-button" 
                        onClick={() => handleRevokeLink(link.id)} 
                        style={{ padding: '6px 12px', fontSize: '12px', margin: 0, backgroundColor: '#dc3545', color: 'white' }}
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '13px', margin: '20px 0' }}>No active secure links generated for this file.</p>
        )}
      </div>

    </Modal>
  );
};